import type { ReactNode } from "react";
import { ASSISTANT_AVATAR } from "@/theme/tokens";
import { AssistantAvatarPointSide, type AvatarRect, type GazeDirection, gazeToward } from "../AssistantAvatar/assistantAvatarGeometry";

const { TOUR_TALK_PER_CHAR, TOUR_TALK_MIN, TOUR_TALK_MAX, TOUR_TALK_DELAY, TOUR_OOPS } = ASSISTANT_AVATAR.MOTION;

/** What a tour guide is doing during one step; the host maps each beat onto its figure's moods. */
export enum WalkthroughGuideBeat {
	Arriving = "arriving",
	Talking = "talking",
	Pointing = "pointing",
	Oops = "oops",
	Done = "done",
}

/** `point`/`gaze` are absent until the target is measured; `point` also on the Done beat. */
export interface WalkthroughGuidePose {
	beat: WalkthroughGuideBeat;
	size: number;
	point?: AssistantAvatarPointSide;
	gaze?: GazeDirection;
}

/** A figure that walks a tour from the step card's header slot. */
export interface WalkthroughGuide {
	size: number;
	render: (pose: WalkthroughGuidePose) => ReactNode;
}

export interface GuideAim {
	point: AssistantAvatarPointSide;
	gaze: GazeDirection;
}

const centerX = (rect: AvatarRect): number => rect.left + rect.width / 2;

export const guidePointSide = (avatar: AvatarRect, target: AvatarRect): AssistantAvatarPointSide =>
	centerX(target) >= centerX(avatar) ? AssistantAvatarPointSide.Right : AssistantAvatarPointSide.Left;

export const guideAim = (avatar: AvatarRect, target: AvatarRect): GuideAim => ({ point: guidePointSide(avatar, target), gaze: gazeToward(avatar, target) });

interface GuideCue {
	beat: WalkthroughGuideBeat;
	atMs: number;
}

export type GuideCues = readonly [GuideCue, ...GuideCue[]];

interface GuideTalkWindow {
	delayMs: number;
	durationMs: number;
}

export interface GuideStepInput {
	textLength: number;
	isLastStep: boolean;
	/** A revisited last step rests instead of celebrating again. */
	hasCelebrated: boolean;
	reducedMotion: boolean;
}

export const talkDuration = (textLength: number): number => Math.min(Math.max(textLength * TOUR_TALK_PER_CHAR, TOUR_TALK_MIN), TOUR_TALK_MAX);

export const talkWindow = (input: GuideStepInput): GuideTalkWindow | null =>
	input.reducedMotion ? null : { delayMs: TOUR_TALK_DELAY, durationMs: talkDuration(input.textLength) };

/** Arrive, talk while the copy reveals, then point — or celebrate once on the last step. Reduced motion cuts to the rest beat. */
export const stepCues = (input: GuideStepInput): GuideCues => {
	const rest = input.isLastStep && !input.hasCelebrated ? WalkthroughGuideBeat.Done : WalkthroughGuideBeat.Pointing;
	const talk = talkWindow(input);
	if (talk === null) return [{ beat: rest, atMs: 0 }];
	return [
		{ beat: WalkthroughGuideBeat.Arriving, atMs: 0 },
		{ beat: WalkthroughGuideBeat.Talking, atMs: talk.delayMs },
		{ beat: rest, atMs: talk.delayMs + talk.durationMs },
	];
};

export const oopsUntil = (atMs: number): number => atMs + TOUR_OOPS;

/** An oops overrides every beat but the celebration while it lasts. */
export const beatAt = (cues: GuideCues, elapsedMs: number, oopsEndsAtMs: number | null): WalkthroughGuideBeat => {
	const scheduled = cues.reduce<WalkthroughGuideBeat>((beat, cue) => (cue.atMs <= elapsedMs ? cue.beat : beat), cues[0].beat);
	if (oopsEndsAtMs !== null && elapsedMs < oopsEndsAtMs && scheduled !== WalkthroughGuideBeat.Done) return WalkthroughGuideBeat.Oops;
	return scheduled;
};

export const nextChangeAt = (cues: GuideCues, elapsedMs: number, oopsEndsAtMs: number | null): number | null => {
	const upcoming = [...cues.map((cue) => cue.atMs), ...(oopsEndsAtMs === null ? [] : [oopsEndsAtMs])].filter((atMs) => atMs > elapsedMs);
	return upcoming.length === 0 ? null : Math.min(...upcoming);
};
